import { useEffect, useState } from "react";
import { AllergenFilterSwitch } from "./AllergenFilter.tsx";
import type { FormEvent, ReactNode } from "react";
import { LANG_LABELS, PDF_SLOT_LIMITS, cardGaps, cardLangOf, designFor, guessedOn, guessedQuestion, editsCatalog, editsMenus, entranceConflicts, missingFromNet, plainPrice, freezeForNet, hasDrinks, marksSoldOut, menuView, ownsBusiness, publishes, textStatus, withDrinksOf } from "@speisekarte/core";
import type { MenuInput } from "@speisekarte/core";
import { drinksInPlay } from "./Dishes.tsx";
import { qrPng, qrSvg } from "../qr.ts";
import { downloadBlob } from "../print.ts";
import { entranceNow, printPdf, publishPdfSlot } from "../output.ts";
import { loadLogo } from "../logo.ts";
import { redirectPage } from "../redirect-page.ts";
import { outdated, pdfMarkOf, recommendationOld, slotOld, slotPrintNow } from "../outside.ts";
import { ApiError, api, dishesById, messageOf, store, textsById, useStore } from "../store.ts";
import { useDraft, useKept } from "../kept.ts";
import { reviewHref, textSourceOf, textsAction, textsCount, toReview } from "./TextWork.tsx";
import { useModel } from "../model.ts";
import type { Data } from "../store.ts";
import { Toast, useToast } from "./Toast.tsx";
import { NowLine, PlannedList, WhenChoice, whenAt } from "./Times.tsx";
import type { When } from "./Times.tsx";
import { extendedOn, timedOn, whenText } from "../timing.ts";
import { PlanCard } from "./Plan.tsx";

const when = (at: number) => new Date(at).toLocaleString("de-DE", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

export { leadNow, outdated, recommendationOld } from "../outside.ts";

/**
 * The languages of a menu on the net (Martin, 01.11.2026): German and those it is wholly in, as the
 * server freezes them (`freezeForNet`); and what each other one still lacks. With the drinks of
 * „Getränke aus Speisekarte …“ (S22). Null without further languages.
 */
function netGaps(data: Data, menu: MenuInput & { id: string }) {
  const langs = data.settings.languages ?? [];
  if (!langs.length) return null;
  const from = data.settings.drinksMenu;
  const m = withDrinksOf(menu, from && from !== menu.id ? (data.menus.find((x) => x.id === from) ?? null) : null);
  return cardGaps(menuView(m, dishesById(data), data.allergens, textsById(data), data.tagIcons), dishesById(data), langs, data.phrases ?? {});
}

const listed = (words: string[]) => (words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} und ${words.at(-1)}`);

/**
 * One line per language for the menu on the net: in which it is (or will be after publishing), what
 * the others lack, with a way to the translating in step 2 of that menu in that language.
 */
export function NetLangs({ data, menu, now }: { data: Data; menu: MenuInput & { id: string }; now: boolean }) {
  const model = useModel();
  const gaps = netGaps(data, menu);
  if (!gaps) return null;
  const whole = gaps.filter((g) => g.dishes + g.texts === 0).map((g) => LANG_LABELS[g.lang]);
  const status = textStatus(textSourceOf(data, model), { menu });
  const review = new Map(gaps.map((g) => [g.lang, toReview(status.counts[g.lang])]));
  return (
    <div className="net-langs" data-testid="net-langs">
      <p className="hint">
        {now ? "Im Netz" : "Nach dem Veröffentlichen im Netz"} auf {listed(["Deutsch", ...whole])}.
        {whole.length < gaps.length && " Eine Sprache kommt erst ins Netz, wenn die Karte ganz übersetzt ist; dann fragt die App nach dem Veröffentlichen."}
      </p>
      {gaps
        .filter((g) => g.dishes + g.texts > 0)
        .map((g) => (
          <p key={g.lang} className="warn" data-testid={`net-lang-${g.lang}`}>
            {LANG_LABELS[g.lang]}: {textsCount(g.dishes + g.texts)} nicht übersetzt.{" "}
            {menu.id !== "neu" && (
              <a href={reviewHref(menu.id, { lang: g.lang, flow: model })} data-testid={`net-lang-${g.lang}-go`}>
                {textsAction(data, model, status)}
              </a>
            )}
          </p>
        ))}
      {/* Decided (S61): the model's translations go on the net, but stay visibly to be looked through. */}
      {menu.id !== "neu" &&
        gaps
          .filter((g) => g.dishes + g.texts === 0 && (review.get(g.lang) ?? 0) > 0)
          .map((g) => (
            <p key={`r-${g.lang}`} className="hint" data-testid={`net-lang-${g.lang}-review`}>
              {LANG_LABELS[g.lang]}: {textsCount(review.get(g.lang) ?? 0)} nicht durchgesehen. <a href={reviewHref(menu.id, { lang: g.lang, flow: model })}>{textsAction(data, model, status)}</a>
            </p>
          ))}
    </div>
  );
}

/**
 * After texts were checked or translated (S61; Martin, 02.11.2026): what guests still see in its old
 * state, by name – the QR-Code-Speisekarte and the Empfehlungskarte above it – and the way to take the
 * changes over right there; afterwards, that it is done. PDF links are not compared yet (their own package).
 */
export function NetBehind({ data }: { data: Data }) {
  const role = useStore().role;
  const [state, setState] = useState<{ busy?: boolean; done?: string; error?: string }>({});
  if (state.done)
    return (
      <p className="ok-text" data-testid="net-behind-done">
        {state.done}
      </p>
    );
  const pub = data.published ? data.menus.find((m) => m.id === data.published!.menu) : null;
  const net = Boolean(pub) && outdated(data);
  // The PDF links too (S62, Z-0): one rule for all that stands outside.
  const pdfs = (data.pdfSlots ?? []).filter((x) => slotOld(x, data));
  if (!net && !pdfs.length) return null;
  const rec = net && recommendationOld(data) ? data.menus.find((m) => m.id === data.settings.recommendationsMenu) : null;
  const parts = [...(net ? [`der QR-Code-Speisekarte „${pub!.title}“`] : []), ...(rec ? [`der Empfehlungskarte „${rec.title}“`] : []), ...pdfs.map((x) => `im PDF-Link „${x.name}“`)];
  const what = listed(parts);
  const label = net && pdfs.length ? `Übernehmen und ${pdfs.length === 1 ? "PDF" : "PDFs"} neu erzeugen` : net ? "Änderungen übernehmen" : pdfs.length === 1 ? "PDF neu erzeugen" : "PDFs neu erzeugen";
  async function take() {
    if (net) {
      const guessed = guessedOn(pub!, new Map(data.dishes.map((d) => [d.id, d])));
      if (guessed > 0 && !confirm(guessedQuestion(guessed))) return;
    }
    setState({ busy: true });
    try {
      if (net) await store.change(() => api("/api/publish", { body: { menu: pub!.id } }));
      for (const x of pdfs) {
        const now = slotPrintNow(x, store.data ?? data);
        if (now) await publishPdfSlot(now, store.data ?? data, x.id, x.menuId ?? undefined, pdfMarkOf(now, store.data ?? data, cardLangOf(x.lang, data.settings.languages ?? [])));
      }
      setState({ done: `${net ? "Übernommen" : "Erneuert"}: Gäste sehen jetzt den neuen Stand ${what}.` });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <div className="warn" data-testid="net-behind">
      <p style={{ margin: 0 }}>Gäste sehen noch den alten Stand {what}.</p>
      {publishes(role) ? (
        <p className="buttons" style={{ margin: "0.5rem 0 0" }}>
          <button className="primary" onClick={() => void take()} disabled={state.busy} data-testid="net-behind-take">
            {state.busy ? "Wird übernommen …" : label}
          </button>
        </p>
      ) : (
        <p className="small" style={{ margin: "0.3rem 0 0" }}>
          Übernehmen können Inhaber und Verwaltung.
        </p>
      )}
      {state.error && <p className="error">{state.error}</p>}
    </div>
  );
}

/** To „Ausverkauft“ (S39), with how many are sold out now. */
function SoldOutLink({ data }: { data: Data }) {
  const now = Date.now();
  const n = data.soldOut.filter((x) => x.until > now).length;
  return (
    <a className="button" href="#/ausverkauft" data-testid="sold-out-link">
      Ausverkauft{n ? ` (${n})` : ""}
    </a>
  );
}

/**
 * „Im Netz“ (S59), by how often it is needed: the QR-Code-Speisekarte (at most one menu, with a QR
 * code that stays; docs/01-architektur.md), its drinks and its Empfehlungskarte, the QR code with the
 * stickers, the PDF links, the own address for both (S40, docs/19-eigene-adresse.md), the views. The logo is part of the style (Design.tsx),
 * the house's name and the lines at the foot are the business's (Konto → Betrieb); the price list at
 * the entrance is paper and stands under „Karten“.
 */
export function Web() {
  const s = useStore();
  const data = s.data!;
  const toast = useToast();
  const keeper = publishes(s.role);
  // The link of the menu on the net: the server names the public address (S30), wherever the app was opened.
  const [link, setLink] = useState<{ token: string | null; url: string | null; domain?: string | null; target?: string } | undefined>(undefined);
  const token = link?.token;
  const [choice, setChoice] = useKept("netz:choice", data.published?.menu ?? data.menus[0]?.id ?? "");
  const published = data.published ? data.menus.find((m) => m.id === data.published!.menu) : null;
  const old = outdated(data);
  const url = link?.url ?? null;

  const loadLink = () => api<{ token: string | null; url: string | null; domain: string | null; target: string }>("/api/publish/link").then(setLink, () => setLink({ token: null, url: null }));
  useEffect(() => {
    if (keeper) void loadLink();
  }, [keeper, data.published?.publishedAt, data.settings.ownUrl]);
  // What QR code and stickers show (S40): the own domain, the own address, or the link.
  const shown = link?.domain ? `https://${link.domain}` : data.settings.ownUrl || url;

  const act = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await store.change(fn);
      toast.show(done);
    } catch (err) {
      toast.show(messageOf(err));
    }
  };
  // Später … (S62, Bild 3): another menu at a moment, with the time control.
  const timed = timedOn(data);
  const [later, setLater] = useState<When | null>(null);
  const plan = (id: string, when: When) => {
    const at = whenAt(when, "menu");
    if (at === null || !Number.isFinite(at)) return toast.show("Bitte Tag und Uhrzeit angeben.");
    return act(() => api("/api/plan", { body: { what: "menu", menu: id, at } }), `Geplant: ${whenText(at, Date.now())} als QR-Code-Speisekarte`).then(() => setLater(null));
  };
  const publish = (id: string) => {
    const menu = data.menus.find((m) => m.id === id);
    const guessed = menu ? guessedOn(menu, new Map(data.dishes.map((d) => [d.id, d]))) : 0;
    if (guessed > 0 && !confirm(guessedQuestion(guessed))) return;
    return act(() => api("/api/publish", { body: { menu: id } }), "Veröffentlicht");
  };

  if (!keeper) {
    return (
      <>
      {/* The weekly plan (S62, Z7): the service sees it and plans tomorrow's recommendations. */}
      {extendedOn(data) && <PlanCard data={data} />}
      <section className="card">
        <h2>QR-Code-Speisekarte</h2>
        <p>{published ? `Gäste sehen „${published.title}“, seit ${when(data.published!.publishedAt)}.` : "Gäste sehen zurzeit keine Speisekarte."}</p>
        <p className="hint">Veröffentlichen dürfen Inhaber und Verwaltung.</p>
        {published && marksSoldOut(s.role) && <SoldOutLink data={data} />}
        {published && editsMenus(s.role) && shows(data) && <Recommendations data={data} />}
      </section>
      </>
    );
  }

  return (
    <div className="web">
      {/* Der Wochenplan (S62, Stufe 2; Bild 6, 10): with „Erweitert“ first, the QR-Code-Speisekarte below it is „Sonst“. */}
      {extendedOn(data) && <PlanCard data={data} />}
      <section className="card">
        <h2>{extendedOn(data) && (data.folders ?? []).length ? `QR-Code-Speisekarte · Mappe „${data.settings.otherwiseName || "Abend"}“ (sonst)` : "QR-Code-Speisekarte"}</h2>
        {published ? (
          <div className={`status-line ${old ? "outdated" : "on"}`} data-testid="web-status">
            Gäste sehen: <strong>{published.title}</strong>, seit {when(data.published!.publishedAt)}.
            {old && <div>Seitdem hat sich etwas geändert; Gäste sehen noch den alten Stand.</div>}
          </div>
        ) : (
          <div className="status-line" data-testid="web-status">
            Gäste sehen zurzeit keine Speisekarte, nur „Bitte fragen Sie das Personal“.
          </div>
        )}
        {/* Jetzt (S62, Bild 3): with the time control and a temporary section, what guests see now and next. */}
        {published && timedOn(data) && <NowLine menu={published} />}
        {published && <NetLangs data={data} menu={published} now={!old} />}
        <div className="buttons">
          {published && old && (
            <button className="primary" onClick={() => void publish(published.id)} data-testid="republish">
              Änderungen übernehmen
            </button>
          )}
          {published && (
            <a className="button" href={`/api/publish/preview?menu=${published.id}&workspace=${store.session!.workspaceId}`} target="_blank" rel="noopener">
              So sehen Gäste sie
            </a>
          )}
          {published && <SoldOutLink data={data} />}
          {published && (
            <button className="danger" onClick={() => confirm("Die QR-Code-Speisekarte aus dem Netz nehmen? Gäste sehen dann „Zurzeit keine Speisekarte“; QR-Code und Aufkleber bleiben gültig.") && void act(() => api("/api/publish/withdraw", { body: {} }), "Aus dem Netz genommen")} data-testid="withdraw">
              Aus dem Netz nehmen
            </button>
          )}
        </div>
        {data.menus.length > 0 ? (
          <label>
            {published ? "Andere Speisekarte zeigen" : "Speisekarte zeigen"}
            <span className="row">
              <select value={choice} onChange={(e) => setChoice(e.target.value)} data-testid="publish-choice">
                {data.menus.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
              </select>
              <button className={published ? "" : "primary"} disabled={!choice || choice === published?.id} onClick={() => void publish(choice)} data-testid="publish-chosen">
                Zeigen
              </button>
              {timed && (
                <button disabled={!choice || choice === published?.id} onClick={() => setLater(later ? null : { kind: "tomorrow" })} data-testid="publish-later">
                  Später …
                </button>
              )}
            </span>
          </label>
        ) : (
          <p className="muted">Noch keine Speisekarte gespeichert.</p>
        )}
        {timed && later && (
          <div className="planned-move" data-testid="publish-later-box">
            <WhenChoice what="menu" value={later} onChange={setLater} testid="publish-later-when" />
            <button className="primary" disabled={later.kind === "now"} onClick={() => void plan(choice, later)} data-testid="publish-later-plan">
              Einplanen
            </button>
          </div>
        )}
        {timed && <PlannedList data={data} what="menu" may />}
        {/* Der Allergenfilter für Gäste (S63, Bild 5): off by default, at once on the net. */}
        <AllergenFilterSwitch data={data} shown={[published, data.menus.find((m) => m.id === data.settings.recommendationsMenu)]} />
        <p className="hint">Der QR-Code bleibt derselbe, egal welche Speisekarte du zeigst.</p>
      </section>

      {(drinksInPlay(data) || ownsBusiness(s.role) || data.settings.kellerlog) && <Drinks data={data} owner={ownsBusiness(s.role)} />}
      {published && shows(data) && <Recommendations data={data} />}

      <section className="card">
        <h2>QR-Code und Aufkleber</h2>
        <p className="hint">
          Ein Code für alle Tische, der bleibt.{" "}
          <a href="/tutorials/im-netz#qr-code" target="_blank" rel="noopener" data-testid="qr-tutorial">
            Wie das geht ↗
          </a>
        </p>
        {url && shown ? <Link url={shown} link={url} direct={Boolean(link?.domain)} house={data.settings.house || data.workspace} /> : <p className="muted">{token === undefined ? "Lädt …" : "QR-Code und Link entstehen beim ersten Veröffentlichen."}</p>}
        {url && (
          <details className="fold">
            <summary>QR-Code ersetzen</summary>
            <p className="warn">Nur bei Missbrauch: Der alte Link zeigt danach nichts mehr, und alle gedruckten Aufkleber und Speisekarten mit dem QR-Code sind ungültig.</p>
            <button
              className="danger"
              onClick={async () => {
                const own = data.settings.ownUrl;
                if (!confirm(own ? `Wirklich einen neuen Link erzeugen? Deine eigene Adresse ${own} leitet dann ins Leere: Die Weiterleitung bei deinem Webhoster muss auf den neuen Link zeigen, danach hier neu prüfen.` : "Wirklich einen neuen QR-Code erzeugen? Alle gedruckten QR-Codes werden ungültig.")) return;
                try {
                  setLink(await store.change(() => api<{ token: string; url: string }>("/api/publish/link", { body: { renew: true } })));
                  toast.show("Neuer QR-Code erzeugt");
                } catch (err) {
                  toast.show(messageOf(err));
                }
              }}
            >
              Neuen QR-Code erzeugen
            </button>
          </details>
        )}
      </section>

      <PdfSlots data={data} />
      {url && <OwnAddress link={url} house={data.settings.house || data.workspace} domain={link?.domain ?? null} target={link?.target ?? ""} reload={loadLink} />}
      {url && <Views />}
      <Toast toast={toast} />
    </div>
  );
}

/** The QR code, the address under it, stickers: `url` is what they show (the own address, S40), `link` the menu's link. */
function Link({ url, link, house, direct = false }: { url: string; link: string; house: string; direct?: boolean }) {
  const data = useStore().data!;
  const [copied, setCopied] = useState(false);
  // With the business's logo beside the code instead of its name above it (Martin, 25.10.2026); a tick, not a second button (S59).
  const [withLogo, setWithLogo] = useKept("netz:sticker-logo", false);
  const png = async (px: number) => downloadBlob(await qrPng(url, px), `QR-Code Speisekarte ${px}.png`);
  const stickers = () =>
    void printPdf(withLogo && data.logo ? "Aufkleber Speisekarte mit Logo.pdf" : "Aufkleber Speisekarte.pdf", async () => {
      if (!(withLogo && data.logo)) return (await import("../menuPdf.ts")).stickerPdf(url, house, designFor(data.designs, ""));
      const logo = await loadLogo(data);
      if (!logo) throw new Error("Das Logo ist ohne Netz noch nicht auf diesem Gerät.");
      return (await import("../menuPdf.ts")).stickerPdf(url, house, designFor(data.designs, ""), "Speisekarte", logo);
    }).catch((e) => alert(messageOf(e)));
  return (
    <div>
      <div className="web-qr" dangerouslySetInnerHTML={{ __html: qrSvg(url, "QR-Code der Speisekarte") }} data-testid="web-qr" />
      <p className="web-url">
        <a href={url} target="_blank" rel="noopener" data-testid="web-url">
          {url.replace(/^https?:\/\//, "")}
        </a>
        {url !== link && (
          <span className="hint block" data-testid="web-url-via">
            {direct ? "dieselbe Speisekarte wie" : "leitet weiter zu"} {link.replace(/^https?:\/\//, "")}
          </span>
        )}
      </p>
      <div className="buttons">
        <button onClick={async () => [await navigator.clipboard?.writeText(url).catch(() => undefined), setCopied(true)]}>{copied ? "Kopiert" : "Link kopieren"}</button>
        <button onClick={stickers} data-testid="stickers">
          Aufkleber drucken
        </button>
        {data.logo && (
          <label className="check">
            <input type="checkbox" checked={withLogo} onChange={(e) => setWithLogo(e.target.checked)} data-testid="stickers-logo" />
            mit Logo
          </label>
        )}
      </div>
      <details className="fold">
        <summary>Dateien für die Druckerei</summary>
        <p className="hint">
          <button className="link inline" onClick={() => downloadBlob(new Blob([qrSvg(url)], { type: "image/svg+xml" }), "QR-Code Speisekarte.svg")}>SVG</button> (beliebig groß, für die Druckerei) ·{" "}
          {[512, 1024, 2048].map((px, i) => (
            <span key={px}>
              {i > 0 && " · "}
              <button className="link inline" onClick={() => void png(px)}>
                PNG {px}
              </button>
            </span>
          ))}
        </p>
      </details>
    </div>
  );
}

/**
 * „Eigene Adresse“ (S40, S59; docs/19-eigene-adresse.md): instead of SpeiseKarte's address the QR code,
 * the stickers and the PDF links show one of the house, by one of two ways – a redirect on its own
 * website (the QR code only), or its own domain (the PDF links too). A card of its own after the PDF
 * links (Martin, 01.11.2026), each way a fold, open while it is the one in use. How to set them up at
 * the usual hosts is too much for a hint: the tutorial „Eigene Adresse“ says it step by step. A demo's
 * QR code shows only SpeiseKarte's link (the server refuses it there too): the card only says so.
 */
function OwnAddress({ link, house, domain, target, reload }: { link: string; house: string; domain: string | null; target: string; reload: () => Promise<void> }) {
  const data = useStore().data!;
  const demo = Boolean(store.session?.demo);
  const ours = link.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  const own = data.settings.ownUrl;
  const forwarded = (data.pdfSlots ?? []).filter((s) => s.redirectUrl).map((s) => `„${s.name}“`);
  const now = domain ? (
    <>
      deine Domain <b>{domain}</b>
      {(data.pdfSlots ?? []).length > 0 ? "; die PDF-Links stehen auch dort." : "."}
    </>
  ) : own ? (
    <>
      deine Adresse <b>{own.replace(/^https:\/\//, "")}</b> (Weiterleitung).
    </>
  ) : (
    <>
      den Link von SpeiseKarte ({ours}).
    </>
  );
  return (
    <section className="card own-url" data-testid="own-address">
      <h2>Eigene Adresse</h2>
      <p className="hint">
        QR-Code, Aufkleber und PDF-Links können statt {ours} eine Adresse deines Hauses zeigen. Dann gehören die Aufkleber dir: Wechselst du einmal den Anbieter, bleiben sie gültig.{" "}
        <a href="/tutorials/eigene-adresse" target="_blank" rel="noopener" data-testid="own-address-tutorial">
          Welcher Weg passt und wie es geht ↗
        </a>
      </p>
      {demo ? (
        <p className="hint" data-testid="own-address-demo">
          Im Demo-Zugang nicht möglich: Hier zeigt der QR-Code immer den Link von SpeiseKarte.
        </p>
      ) : (
        <>
          <p className="own-now" data-testid="own-now">
            QR-Code und Aufkleber zeigen {now}
            {!domain && forwarded.length > 0 && ` Eigene Adresse auch für ${forwarded.length === 1 ? "den PDF-Link" : "die PDF-Links"} ${forwarded.join(", ")}.`}
          </p>
          <Redirect link={link} house={house} domain={domain} reload={reload} />
          <Domain domain={domain} target={target} reload={reload} redirects={(own ? 1 : 0) + (data.pdfSlots ?? []).filter((s) => s.redirectUrl).length} />
          <p className="hint">Es gilt immer nur ein Weg: Die Domain ersetzt die Weiterleitungen und umgekehrt; die App fragt vorher.</p>
        </>
      )}
    </section>
  );
}

/**
 * The first way (S40): addresses on the house's own website that redirect – one to the menu's link
 * (QR code and stickers), one to each PDF link's address (Martin, 01.11.2026); each checked by the
 * server before it is shown. Kept, a redirect ends the own domain: one way at a time.
 */
function Redirect({ link, house, domain, reload }: { link: string; house: string; domain: string | null; reload: () => Promise<void> }) {
  const data = useStore().data!;
  const own = data.settings.ownUrl ?? "";
  const slots = (data.pdfSlots ?? []).filter((s) => s.url);
  const active = !domain && Boolean(own || slots.some((s) => s.redirectUrl));
  const [open, setOpen] = useState(active);
  useEffect(() => void (active && setOpen(true)), [active]);
  // A redirect kept ends the domain: asked first; the QR code's address then comes anew.
  const ask = () => !domain || confirm(`Damit endet die eigene Domain ${domain}: QR-Code, Aufkleber und PDF-Links zeigen sie nicht mehr. Weiter?`);
  return (
    <details className="own-way" open={open || undefined} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)} data-testid="own-url">
      <summary>Weg 1: Weiterleitung von deiner Website</summary>
      <p className="hint">
        Eine Adresse auf deiner Website, etwa www.deine-website.de/karte, leitet weiter: eine zur QR-Code-Speisekarte, eine zu jedem PDF-Link. Geht mit jeder Website.{" "}
        <a href="/tutorials/eigene-adresse" target="_blank" rel="noopener" data-testid="own-url-tutorial">
          Schritt für Schritt ↗
        </a>
      </p>
      <RedirectTo
        id="own-url"
        title="QR-Code-Speisekarte"
        target={link}
        saved={own}
        page={() => redirectPage(link, house)}
        placeholder="https://www.deine-website.de/karte"
        ask={ask}
        send={async (url) => {
          const r = await store.change(() => api<{ ownUrl: string; permanent?: boolean }>("/api/publish/own-url", { body: { url } }));
          await reload();
          return { url: r.ownUrl, permanent: r.permanent };
        }}
        done={(url) => (url ? `Geprüft: ${url.replace(/^https:\/\//, "")} führt zu deiner Speisekarte. QR-Code und Aufkleber zeigen jetzt diese Adresse.` : "QR-Code und Aufkleber zeigen wieder den Link von SpeiseKarte.")}
        clear="Wieder den Link von SpeiseKarte verwenden"
      />
      {slots.map((slot) => (
        <RedirectTo
          key={slot.id}
          id={`pdf-own-${slot.slug}`}
          title={`PDF-Link „${slot.name}“`}
          target={slot.url!}
          saved={slot.redirectUrl ?? ""}
          page={() => redirectPage(slot.url!, house, slot.name)}
          placeholder={`https://www.deine-website.de/${slot.slug}.pdf`}
          ask={ask}
          send={async (url) => {
            const r = await store.change(() => api<{ redirectUrl: string; permanent?: boolean }>(`/api/pdf-slots/${slot.id}/own-url`, { body: { url } }));
            await reload();
            return { url: r.redirectUrl, permanent: r.permanent };
          }}
          done={(url) => (url ? `Geprüft: ${url.replace(/^https:\/\//, "")} führt zum PDF „${slot.name}“. Diese Adresse kannst du jetzt überall verlinken.` : `„${slot.name}“ hat wieder nur die Adresse von SpeiseKarte.`)}
          clear="Weiterleitung entfernen"
        />
      ))}
      {slots.length === 0 && <p className="hint">PDF-Links legst du oben unter „PDF-Links für deine Website“ an; dann steht hier auch für sie ein Feld.</p>}
    </details>
  );
}

/** One redirect of the first way: where it must point, the page for a web space without settings, the address and its check. */
function RedirectTo({ id, title, target, saved, page, placeholder, ask, send, done, clear }: { id: string; title: string; target: string; saved: string; page: () => string; placeholder: string; ask: () => boolean; send: (url: string) => Promise<{ url: string; permanent?: boolean }>; done: (url: string) => string; clear: string }) {
  const [value, setValue] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // A message belongs to the address it was about: taken away elsewhere (the own domain), it goes.
  const [state, setState] = useState<{ ok?: string; warn?: string; error?: string; about?: string }>({});
  useEffect(() => {
    setValue(saved);
    setState((s) => (s.about === undefined || s.about === saved ? s : {}));
  }, [saved]);
  async function check(raw: string) {
    if (raw && !ask()) return;
    setBusy(true);
    setState({});
    try {
      const r = await send(raw);
      setState({ ok: done(r.url), about: r.url, ...(r.url && r.permanent ? { warn: "Auf dem Weg steht eine dauerhafte Weiterleitung (301). Handys merken sich die; ändert sich später etwas, kommen sie nicht mehr an. Bitte bei deinem Webhoster auf „temporär“ (302) umstellen." } : {}) });
    } catch (err) {
      setState({ error: messageOf(err) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="own-to" data-testid={`${id}-way`}>
      <h3>{title}</h3>
      <p className="hint">
        Weiterleiten auf: <code data-testid={`${id}-target`}>{target}</code>{" "}
        <button className="link inline" onClick={async () => [await navigator.clipboard?.writeText(target).catch(() => undefined), setCopied(true)]}>
          {copied ? "kopiert" : "kopieren"}
        </button>{" "}
        ·{" "}
        <button className="link inline" onClick={() => downloadBlob(new Blob([page()], { type: "text/html" }), "index.html")} data-testid={`${id}-page`}>
          HTML-Datei herunterladen
        </button>
      </p>
      <label>
        Deine Adresse
        <span className="row">
          <input type="url" inputMode="url" placeholder={placeholder} value={value} onChange={(e) => setValue(e.target.value)} data-testid={`${id}-input`} />
          <button className="primary" disabled={busy || !value.trim() || value.trim() === saved} onClick={() => void check(value)} data-testid={`${id}-check`}>
            {busy ? "Prüft …" : "Prüfen"}
          </button>
        </span>
      </label>
      {saved && (
        <button className="link" disabled={busy} onClick={() => void check("")} data-testid={`${id}-clear`}>
          {clear}
        </button>
      )}
      {state.error && <p className="error-box" data-testid={`${id}-error`}>{state.error}</p>}
      {state.ok && <p className="ok-text" data-testid={`${id}-ok`}>{state.ok}</p>}
      {state.warn && <p className="warn">{state.warn}</p>}
    </div>
  );
}

/**
 * The second way (S40): `karte.bodega-la-esquina.de` by a CNAME to SpeiseKarte; the menu then stands
 * there itself, without a redirect, with a certificate Caddy gets for it. The consent first: the name
 * stands in the public logs of certificates.
 */
function Domain({ domain, target, reload, redirects }: { domain: string | null; target: string; reload: () => Promise<void>; redirects: number }) {
  const [value, setValue] = useState(domain ?? "");
  const [consent, setConsent] = useState(Boolean(domain));
  const [open, setOpen] = useState(Boolean(domain));
  useEffect(() => void (domain && setOpen(true)), [domain]);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  useEffect(() => setValue(domain ?? ""), [domain]);
  async function check(raw: string) {
    // The domain ends the redirects: asked first.
    if (raw && redirects > 0 && !confirm(redirects === 1 ? "Damit endet deine Weiterleitung: Die Domain tritt an ihre Stelle. Weiter?" : `Damit enden deine ${redirects} Weiterleitungen: Die Domain tritt an ihre Stelle. Weiter?`)) return;
    setBusy(true);
    setState({});
    try {
      const r = await store.change(() => api<{ domain: string | null }>("/api/publish/own-domain", { body: { domain: raw, consent } }));
      // The domain is no change of the business (no new version): the PDF links' addresses under it only come with all of it.
      await Promise.all([reload(), store.refresh(true)]);
      setState({ ok: r.domain ? `Geprüft: ${r.domain} zeigt auf SpeiseKarte. QR-Code und Aufkleber zeigen jetzt https://${r.domain}; das Zertifikat kommt beim ersten Aufruf von selbst.` : "Die eigene Domain ist entfernt. QR-Code und Aufkleber zeigen wieder den Link von SpeiseKarte." });
    } catch (err) {
      setState({ error: messageOf(err) });
    } finally {
      setBusy(false);
    }
  }
  const sub = value.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").split(".")[0] || "karte";
  return (
    <details className="own-way" open={open || undefined} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)} data-testid="own-domain">
      <summary>Weg 2: Eigene Domain</summary>
      <p className="hint">
        Die QR-Code-Speisekarte steht direkt unter einer Subdomain von dir, etwa karte.deine-website.de, ohne Umweg; die PDF-Links auch (karte.deine-website.de/mittagskarte.pdf). Dafür brauchst du Zugang zu den DNS-Einstellungen deiner Domain.{" "}
        <a href="/tutorials/eigene-adresse#domain" target="_blank" rel="noopener" data-testid="own-domain-tutorial">
          Schritt für Schritt ↗
        </a>
      </p>
      <p className="hint">
        Der Eintrag: Typ <b>CNAME</b>, Name <b>{sub}</b>, Ziel <code data-testid="own-domain-target">{target}</code>
      </p>
      <label className="check">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="own-domain-consent" />
        Ich verwalte diese Domain und erlaube SpeiseKarte, dafür ein Sicherheitszertifikat (Let’s Encrypt) ausstellen zu lassen. Der Name steht dann im öffentlichen Verzeichnis der Zertifikate.
      </label>
      <label>
        Deine Domain
        <span className="row">
          <input type="text" inputMode="url" autoCapitalize="off" placeholder="karte.deine-website.de" value={value} onChange={(e) => setValue(e.target.value)} data-testid="own-domain-input" />
          <button className="primary" disabled={busy || !consent || !value.trim() || value.trim() === domain} onClick={() => void check(value)} data-testid="own-domain-check">
            {busy ? "Prüft …" : "Prüfen"}
          </button>
        </span>
      </label>
      {domain && (
        <button className="link" disabled={busy} onClick={() => void check("")} data-testid="own-domain-clear">
          Eigene Domain entfernen
        </button>
      )}
      {state.error && <p className="error-box" data-testid="own-domain-error">{state.error}</p>}
      {state.ok && <p className="ok-text" data-testid="own-domain-ok">{state.ok}</p>}
    </details>
  );
}

/** Views per day, as KellerLog shows them: today, 7 and 30 days, a bar per day; a tile of its own (S59). */
function Views() {
  const [days, setDays] = useState<{ day: string; count: number }[] | null>(null);
  useEffect(() => {
    api<{ days: { day: string; count: number }[] }>("/api/publish/views").then((r) => setDays(r.days), () => setDays(null));
  }, []);
  if (!days) return null;
  const sum = (n: number) => days.slice(-n).reduce((a, d) => a + d.count, 0);
  const max = Math.max(1, ...days.map((d) => d.count));
  return (
    <section className="card web-views">
      <h2>Aufrufe</h2>
      <div className="web-views-stats">
        <span>
          <b>{sum(1)}</b>heute
        </span>
        <span>
          <b>{sum(7)}</b>7 Tage
        </span>
        <span>
          <b>{sum(30)}</b>30 Tage
        </span>
      </div>
      <div className="web-views-bars" aria-hidden="true">
        {days.map((d) => (
          <span key={d.day} title={`${d.day}: ${d.count}`}>
            <i style={{ height: `${(d.count / max) * 100}%` }} />
          </span>
        ))}
      </div>
      <div className="web-views-axis muted">
        <span>vor 30 Tagen</span>
        <span>heute</span>
      </div>
      <p className="hint">Gezählt wird jeder Aufruf der QR-Code-Speisekarte (anonymisiert). Vorschauen in Chats und Suchmaschinen zählen nicht.</p>
    </section>
  );
}

/**
 * „Preisaushang am Eingang“ (S42, § 13 Abs. 2 PAngV), once a menu hangs there; under „Karten“ (S59), it is paper: whether the paper still
 * shows what the catalogue says; prices of the menu on the net (with its drinks and recommendations)
 * that differ from it; dishes of the list that this whole menu does not have (a hint, nothing more).
 */
export function Entrance({ data }: { data: Data }) {
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  const menu = data.menus.find((m) => m.id === data.settings.entranceMenu);
  if (!menu) return null;
  const dishes = dishesById(data);
  const printed = data.settings.entrancePrinted ?? null;
  const old = !printed || printed.menu !== menu.id || printed.digest !== entranceNow(data);
  // The whole menu guests get: the one on the net, its drinks, the recommendations.
  const pub = data.published ? (data.menus.find((m) => m.id === data.published!.menu) ?? null) : null;
  const whole = pub ? [pub, ...[data.settings.drinksMenu, data.settings.recommendationsMenu].flatMap((id) => data.menus.filter((m) => id && m.id === id))] : [];
  const lines = whole
    .filter((m) => m.id !== menu.id)
    .flatMap((m) => {
      const c = entranceConflicts(m, menu, dishes);
      return [...c.dishes.flatMap((d) => [...(d.to !== null ? [`„${d.name}“: am Eingang ${plainPrice(d.from)}\u00a0€, auf „${m.title}“ ${plainPrice(d.to)}\u00a0€`] : []), ...d.variants.map((v) => `„${d.name}“, ${v.label}: am Eingang ${plainPrice(v.from)}\u00a0€, auf „${m.title}“ ${plainPrice(v.to)}\u00a0€`)]), ...c.sets.map((x) => `Menü „${x.title}“: am Eingang ${plainPrice(x.entrance)}\u00a0€, auf „${m.title}“ ${plainPrice(x.here)}\u00a0€`)];
    });
  const missing = whole.length ? missingFromNet(menu, whole, dishes) : [];
  async function choose(id: string) {
    try {
      await store.change(() => api("/api/entrance", { body: { menu: id || null } }));
      setState({ ok: id ? "Hängt jetzt am Eingang aus. Bitte drucken." : "Kein Aushang mehr gewählt" });
    } catch (err) {
      setState({ error: err instanceof ApiError && err.code === "price_diffs" ? "Auf dieser Speisekarte stehen andere Preise als im Katalog. Bitte in ihr unter „Drucken & Netz“ „Als Aushang am Eingang“ wählen; dort lässt sich entscheiden, welche gelten." : messageOf(err) });
    }
  }
  return (
    <section className="card" data-testid="entrance">
      <h2>Preisaushang am Eingang</h2>
      <p className="hint">
        Pflicht nach der Preisangabenverordnung. Am Eingang hängt <a href={`#/speisekarte/${menu.id}`}>„{menu.title}“</a>, mit den Preisen aus dem Katalog.
      </p>
      <p className={old ? "warn" : "hint"} data-testid="entrance-state">
        {old ? (printed ? "Seit dem letzten Druck geändert: bitte neu drucken und aushängen." : "Noch nicht gedruckt: bitte drucken und aushängen.") : `Zuletzt gedruckt am ${when(printed!.at)}; stimmt mit dem Katalog überein.`}
      </p>
      {lines.length > 0 && (
        <p className="warn" data-testid="entrance-net-conflicts">
          Diese Preise weichen von der QR-Code-Speisekarte ab: {lines.join("; ")}.
        </p>
      )}
      {missing.length > 0 && (
        <p className="hint" data-testid="entrance-missing">
          Am Eingang, aber nicht auf der QR-Code-Speisekarte: {missing.map((d) => d.name).join(", ")}.
        </p>
      )}
      {editsCatalog(store.role) && pub && (
        <div className="buttons">
          <a className="button" href={`#/speisekarte/neu?von=${pub.id}&aushang=1`} data-testid="new-entrance-from-net">
            Neuer Aushang als Auszug der QR-Code-Speisekarte
          </a>
        </div>
      )}
      {editsCatalog(store.role) && (
        <label>
          Aushang
          <select value={menu.id} onChange={(e) => void choose(e.target.value)} data-testid="entrance-menu">
            <option value="">keiner</option>
            {data.menus.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title}
              </option>
            ))}
          </select>
        </label>
      )}
      {state.error && <p className="error-box">{state.error}</p>}
      {state.ok && <p className="ok-text">{state.ok}</p>}
    </section>
  );
}

/** The Empfehlungskarte shows from a second menu on (S38: from the threshold on), or while some are chosen. */
const shows = (data: Data) => data.menus.length >= 2 || Boolean(data.settings.recommendationsMenu);

/**
 * „Empfehlungskarte“ (S37, L2; the name S59): another menu above the QR-Code-Speisekarte, in a box of
 * its own – the recommendations of the day, the wines of the week. Swapping it is on the net at once,
 * without publishing the menu again; the service may too (S10). Printed on its own, as an insert.
 */
function Recommendations({ data }: { data: Data }) {
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  const pub = data.published!.menu;
  const chosen = data.settings.recommendationsMenu ?? "";
  const gone = chosen !== "" && !data.menus.some((m) => m.id === chosen);
  async function choose(id: string) {
    try {
      await store.change(() => api("/api/publish/recommendations", { body: { menu: id || null } }));
      setState({ ok: id ? "Gilt sofort" : "Keine Empfehlungskarte mehr" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <section className="card" data-testid="recommendations">
      <h2>Empfehlungskarte</h2>
      <p className="hint">Eine Empfehlungskarte ist eine Karte mit z. B. Tagesempfehlungen und/oder den Weinen der Woche. Der QR-Code-Link zeigt die Empfehlungskarte vor der QR-Code-Speisekarte (wie ein Einlegeblatt). Ein Wechsel gilt sofort.</p>
      <label>
        Empfehlungskarte
        <select value={gone ? "" : chosen} onChange={(e) => void choose(e.target.value)} data-testid="recommendations-menu">
          <option value="">keine</option>
          {data.menus
            .filter((m) => m.id !== pub)
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.title}
              </option>
            ))}
        </select>
      </label>
      {gone && <p className="warn">Die gewählte Empfehlungskarte gibt es nicht mehr.</p>}
      {/* Geplant (S62, Bild 4): what goes on the net by itself; planned in step 3 of a menu. */}
      {timedOn(data) && <PlannedList data={data} what="recommendations" may />}
      {state.error && <p className="error-box">{state.error}</p>}
      {state.ok && <p className="ok-text">{state.ok}</p>}
      <div className="buttons">
        <a className="button" href={chosen && !gone ? `#/speisekarte/neu?von=${chosen}&empfehlung=1` : "#/speisekarte/neu?empfehlung=1"} data-testid="new-recommendation">
          Neue Empfehlungskarte anlegen
        </a>
      </div>
    </section>
  );
}

type DrinksChoice = "menu" | "fixed" | "kellerlog" | "none";

/**
 * „Getränke“ (S59): what stands under „Getränke“ on the QR-Code-Speisekarte, one choice instead of two
 * tiles – the drinks of the menu shown (S22), a fixed drinks menu beside the changing lunch (S22),
 * KellerLog's card (S7), none. Before it was chosen (`drinksFrom` empty) the old rule holds and shows
 * as what it does: the own drinks, else KellerLog's. Only an owner changes the link to KellerLog.
 *
 * One rule on this card (Martin, 01.11.2026): what is chosen here guests see at once, and the line at
 * the bottom says what they see. A fixed drinks menu is frozen with the menu (S22), so the server
 * freezes the published menu anew; other changes of that menu then go too, which is asked first.
 */
function Drinks({ data, owner }: { data: Data; owner: boolean }) {
  const { drinksMenu, kellerlog } = data.settings;
  const from = data.settings.drinksFrom ?? "";
  const withDrinks = data.menus.filter((m) => hasDrinks(m));
  const pub = data.published ? data.menus.find((m) => m.id === data.published!.menu) : null;
  const gone = drinksMenu !== "" && !data.menus.some((m) => m.id === drinksMenu);
  const chosen: DrinksChoice =
    from === "kellerlog" || from === "none" ? from : from === "" && kellerlog && !drinksMenu && !(pub && hasDrinks(pub)) ? "kellerlog" : drinksMenu && !gone ? "fixed" : "menu";
  const [picked, setPicked] = useState<DrinksChoice>(chosen);
  useEffect(() => setPicked(chosen), [chosen]);
  const [fixed, setFixed] = useState(drinksMenu && !gone ? drinksMenu : (withDrinks.find((m) => m.id !== pub?.id)?.id ?? withDrinks[0]?.id ?? ""));
  const [link, setLink] = useDraft("kellerlog", kellerlog, () => kellerlog);
  const [shown, setShown] = useState<boolean | null>(null);
  const [error, setError] = useState("");
  const [removed, setRemoved] = useState(false);
  // Whether KellerLog's card is there: asked anew for every link, and „wird geprüft“ until the server fetched it.
  useEffect(() => {
    setShown(null);
    if (kellerlog) void api<{ shown: boolean }>("/api/publish/drinks").then((r) => setShown(r.shown), () => setShown(null));
  }, [kellerlog]);
  async function run(fn: () => Promise<unknown>) {
    setError("");
    setRemoved(false);
    try {
      await store.change(fn);
      return true;
    } catch (err) {
      setError(messageOf(err));
      setPicked(chosen);
      return false;
    }
  }
  /** On the net at once; a new fixed drinks menu freezes the menu shown anew, with its other changes. */
  function apply(c: DrinksChoice, menu = fixed) {
    setPicked(c);
    if (c === "kellerlog" && !kellerlog) return;
    if (c === "fixed" && !menu) return;
    const next = c === "fixed" ? menu : c === "menu" ? "" : drinksMenu;
    if (pub && next !== drinksMenu && outdated(data) && !confirm(`Die Getränke wechseln sofort. Dabei gehen auch die übrigen Änderungen an „${pub.title}“ ins Netz. Weiter?`)) return setPicked(chosen);
    void run(() => api("/api/publish/drinks", { body: c === "kellerlog" || c === "none" ? { from: c } : { from: "menu", menu: next } }));
  }
  async function connect(e: FormEvent) {
    e.preventDefault();
    // Without the link the drinks of the menu again.
    if (await run(() => api("/api/settings", { method: "PUT", body: link ? { kellerlog: link, drinksFrom: "kellerlog" } : { kellerlog: "", drinksFrom: "menu" } }))) setRemoved(!link);
  }
  const option = (c: DrinksChoice, label: string, more?: ReactNode) => (
    <label className={picked === c ? "on" : ""}>
      <input type="radio" name="drinks-from" checked={picked === c} onChange={() => apply(c)} data-testid={`drinks-${c}`} />
      <span>
        {label}
        {picked === c && more}
      </span>
    </label>
  );
  const fixedTitle = data.menus.find((m) => m.id === drinksMenu)?.title;
  // What guests see now, from what is saved: the one line that answers „is it on?“; with nothing on the net none.
  const kellerlogOff = chosen === "kellerlog" && shown === false;
  const now = !pub
    ? ""
    : chosen === "none"
      ? "Gäste sehen keine Getränke."
      : chosen === "kellerlog"
        ? kellerlogOff
          ? "Gäste sehen keine Getränke: In KellerLog ist zurzeit keine Getränkekarte veröffentlicht."
          : `Gäste sehen die Getränkekarte aus KellerLog${shown === null ? " (wird geprüft …)" : "."}`
        : chosen === "fixed"
          ? `Gäste sehen die Getränke von „${fixedTitle}“.`
          : hasDrinks(pub)
            ? `Gäste sehen die Getränke von „${pub.title}“.`
            : `Gäste sehen keine Getränke: „${pub.title}“ hat keine.`;
  const waiting = picked !== chosen ? (picked === "kellerlog" ? "Noch nicht aktiv: den Link eintragen und „Anbinden“." : picked === "fixed" && !fixed ? "Noch nicht aktiv: erst eine Speisekarte mit Getränken anlegen." : "") : "";
  return (
    <section className="card" data-testid="drinks-from">
      <h2>Getränke der QR-Code-Speisekarte</h2>
      <p className="hint">Was Gäste auf der QR-Code-Speisekarte unter „Getränke“ sehen. Eine Wahl gilt sofort.</p>
      <fieldset className="choice vertical drinks-choice" aria-label="Getränke">
        {option("menu", "die Getränke der gezeigten Speisekarte")}
        {withDrinks.length > 0 &&
          option(
            "fixed",
            "eine feste Getränkekarte",
            <select value={fixed} onChange={(e) => [setFixed(e.target.value), apply("fixed", e.target.value)]} data-testid="drinks-from-menu">
              {withDrinks.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.title}
                </option>
              ))}
            </select>,
          )}
        {(owner || kellerlog) &&
          option(
            "kellerlog",
            "die Getränkekarte aus KellerLog",
            owner ? (
              <form className="row" noValidate onSubmit={connect}>
                <input value={link} onChange={(e) => [setLink(e.target.value), setError("")]} placeholder="https://kellerlog.mzapps.cloud/k/…" inputMode="url" aria-label="Link der Getränkekarte aus KellerLog" data-testid="kellerlog-link" />
                <button className={kellerlog ? "" : "primary"} disabled={link === kellerlog}>
                  {link || !kellerlog ? "Anbinden" : "Lösen"}
                </button>
              </form>
            ) : (
              <span className="hint">{kellerlog}</span>
            ),
          )}
        {option("none", "keine")}
      </fieldset>
      {picked === "kellerlog" && owner && <p className="hint">Den Link findest du in KellerLog unter Getränkekarte → „Karte im Netz“.</p>}
      {gone && <p className="warn">Die feste Getränkekarte gibt es nicht mehr; Gäste sehen die Getränke der gezeigten Speisekarte.</p>}
      {error && <p className="error-box">{error}</p>}
      {waiting && <p className="warn" data-testid="drinks-waiting">{waiting}</p>}
      {removed && <p className="ok-text">Anbindung entfernt</p>}
      {now && (
        <p className={kellerlogOff ? "warn" : "hint"} data-testid="drinks-now">
          {now}
        </p>
      )}
    </section>
  );
}

/**
 * „PDF-Links für deine Website“ (S54; the names S59): fixed addresses for PDFs, linked once from the
 * house's own website; a menu goes into one in step 3 („In PDF-Link legen“), the address stays. Folded
 * while there is none.
 */
function PdfSlots({ data }: { data: Data }) {
  const role = useStore().role;
  const slots = data.pdfSlots ?? [];
  const [open, setOpen] = useState(slots.length > 0);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  useEffect(() => {
    if (slots.length > 0) setOpen(true);
  }, [slots.length]);
  async function run(fn: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setState({});
    try {
      await store.change(fn);
      setState({ ok });
      return true;
    } catch (err) {
      setState({ error: messageOf(err) });
      return false;
    } finally {
      setBusy(false);
    }
  }
  const copy = async (url: string) => {
    await navigator.clipboard?.writeText(url).catch(() => undefined);
    setCopied(url);
  };
  const full = slots.length >= PDF_SLOT_LIMITS.perBusiness;
  return (
    <section className="card">
      <details className="pdf-slots" open={open || undefined} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)} data-testid="pdf-slots">
        <summary>PDF-Links für deine Website</summary>
        <p className="hint">
          Feste Adressen für PDFs auf deiner Website, etwa die Mittagskarte der Woche; unter „Eigene Adresse“ (unten) auch eine Adresse deiner Website.{" "}
          <a href="/tutorials/im-netz#pdf-links" target="_blank" rel="noopener" data-testid="pdf-slots-tutorial">
            Wie das geht ↗
          </a>
        </p>
        {slots.length > 0 && (
          <ul className="pdf-slot-list">
            {slots.map((slot) => (
              <li key={slot.id} className="pdf-slot" data-testid={`pdf-slot-${slot.slug}`}>
                <div className="pdf-slot-head">
                  <strong>{slot.name}</strong>
                  {slot.file && slot.publishedAt ? (
                    <span className="pdf-slot-state on">verbunden</span>
                  ) : (
                    <span className="pdf-slot-state">noch leer</span>
                  )}
                </div>
                <p className="pdf-slot-what">
                  {slot.file && slot.publishedAt
                    ? `${slot.menuTitle ? `„${slot.menuTitle}“, ` : ""}${slot.pages === 1 ? "1 Seite" : `${slot.pages} Seiten`}, seit ${when(slot.publishedAt)}${slot.publishedBy ? ` von ${slot.publishedBy}` : ""}`
                    : "Zeigt „folgt in Kürze“, bis du in Schritt 3 eine Speisekarte verbindest."}
                </p>
                {/* Old (S62, Z-0): its menu changed since; the way to make it again right here. */}
                {slotOld(slot, data) && (
                  <div className="warn-box" data-testid="pdf-slot-old">
                    „{slot.menuTitle}“ hat sich geändert; das PDF zeigt noch den alten Stand.
                    {publishes(role) && (
                      <div className="buttons" style={{ marginTop: "0.5rem" }}>
                        <button
                          className="primary"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              const now = slotPrintNow(slot, data);
                              if (now) await publishPdfSlot(now, data, slot.id, slot.menuId ?? undefined, pdfMarkOf(now, data, cardLangOf(slot.lang, data.settings.languages ?? [])));
                            }, "Neu erzeugt. Unter dem Link liegt jetzt der neue Stand.")
                          }
                          data-testid="pdf-slot-renew"
                        >
                          PDF neu erzeugen
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {timedOn(data) && <PlannedList data={data} what="pdf" slot={slot.id} may={publishes(role)} />}
                <code className="pdf-slot-url" data-testid="pdf-slot-url">
                  {slot.url}
                </code>
                {/* The house's own address (S40): by a redirect on its website, or under its own domain. */}
                {slot.redirectUrl && (
                  <>
                    <span className="pdf-slot-also">Auf deiner Website:</span>
                    <code className="pdf-slot-url" data-testid="pdf-slot-redirect">{slot.redirectUrl}</code>
                  </>
                )}
                {slot.ownUrl && (
                  <>
                    <span className="pdf-slot-also">Auch unter deiner Domain:</span>
                    <code className="pdf-slot-url" data-testid="pdf-slot-domain">{slot.ownUrl}</code>
                  </>
                )}
                <div className="pdf-slot-actions">
                  <button onClick={() => void copy(slot.url!)}>{copied === slot.url ? "Kopiert" : "Link kopieren"}</button>
                  {slot.redirectUrl && <button onClick={() => void copy(slot.redirectUrl)}>{copied === slot.redirectUrl ? "Kopiert" : "Website-Link kopieren"}</button>}
                  {slot.ownUrl && <button onClick={() => void copy(slot.ownUrl!)}>{copied === slot.ownUrl ? "Kopiert" : "Domain-Link kopieren"}</button>}
                  <a className="button" href={slot.url} target="_blank" rel="noopener">
                    Öffnen ↗
                  </a>
                  <span className="pdf-slot-more">
                    <button
                      disabled={busy}
                      onClick={() => {
                        const next = prompt("Neuer Name des PDF-Links (die Adresse bleibt):", slot.name);
                        if (next && next.trim() && next.trim() !== slot.name) void run(() => api(`/api/pdf-slots/${slot.id}`, { method: "PATCH", body: { name: next.trim() } }), "Umbenannt");
                      }}
                      data-testid="pdf-slot-rename"
                    >
                      Umbenennen
                    </button>
                    <button
                      className="danger"
                      disabled={busy}
                      onClick={() => confirm(`PDF-Link „${slot.name}“ löschen? Die Adresse zeigt danach nichts mehr; Links auf deiner Website gehen ins Leere.`) && void run(() => api(`/api/pdf-slots/${slot.id}`, { method: "DELETE" }), "Gelöscht")}
                      data-testid="pdf-slot-delete"
                    >
                      Löschen
                    </button>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
        {full ? (
          <p className="hint">Höchstens {PDF_SLOT_LIMITS.perBusiness} PDF-Links.</p>
        ) : (
          <form
            className="pdf-slot-new"
            onSubmit={async (e: FormEvent) => {
              e.preventDefault();
              if (await run(() => api("/api/pdf-slots", { body: { name: name.trim() } }), "Angelegt. Die Adresse steht; das PDF verbindest du in Schritt 3.")) setName("");
            }}
          >
            <label>
              Neuer PDF-Link
              <span className="row">
                <input type="text" maxLength={PDF_SLOT_LIMITS.name} placeholder="etwa Mittagskarte" value={name} onChange={(e) => setName(e.target.value)} data-testid="pdf-slot-name" />
                <button className="primary" type="submit" disabled={busy || !name.trim()} data-testid="pdf-slot-add">
                  Anlegen
                </button>
              </span>
            </label>
          </form>
        )}
        {state.error && <p className="error-box" data-testid="pdf-slots-error">{state.error}</p>}
        {state.ok && <p className="ok-text" data-testid="pdf-slots-ok">{state.ok}</p>}
      </details>
    </section>
  );
}
